<script setup lang="ts">
/**
 * ToastContainer — 全局 Toast 容器
 *
 * 挂在 App.vue 根部，监听 _toastStore.items 渲染所有 toast。
 * 按 position 分三区（top / center / bottom）各自独立排列。
 * 动画：CSS Transition Group（slide-down + fade-in 进入，slide-up + fade-out 退出）
 */
import { computed } from 'vue'
import { _toastStore, useToast } from './useToast'
import ToastItem from './ToastItem.vue'

defineOptions({ name: 'ToastContainer' })

const toast = useToast()

const topItems    = computed(() => _toastStore.items.filter((i) => i.position === 'top'))
const centerItems = computed(() => _toastStore.items.filter((i) => i.position === 'center'))
const bottomItems = computed(() => _toastStore.items.filter((i) => i.position === 'bottom'))

function onDismiss(id: string) {
  toast.dismiss(id)
}
</script>

<template>
  <!-- 固定全屏覆盖层（pointer-events-none 避免遮挡交互） -->
  <div
    class="pointer-events-none fixed inset-0 z-[9999] flex flex-col"
    aria-live="polite"
    aria-atomic="false"
    data-testid="toast-container"
  >
    <!-- TOP 区 -->
    <div class="flex flex-col items-center gap-3 px-8 pt-8" data-testid="toast-region-top">
      <TransitionGroup name="toast-slide-down" tag="div" class="flex w-full max-w-[900px] flex-col gap-3">
        <ToastItem
          v-for="item in topItems"
          :key="item.id"
          :item="item"
          @dismiss="onDismiss"
        />
      </TransitionGroup>
    </div>

    <!-- CENTER 区（flex-1 + justify-center） -->
    <div class="flex flex-1 flex-col items-center justify-center px-8" data-testid="toast-region-center">
      <TransitionGroup name="toast-fade" tag="div" class="flex w-full max-w-[900px] flex-col gap-3">
        <ToastItem
          v-for="item in centerItems"
          :key="item.id"
          :item="item"
          @dismiss="onDismiss"
        />
      </TransitionGroup>
    </div>

    <!-- BOTTOM 区 -->
    <div class="flex flex-col items-center gap-3 px-8 pb-8" data-testid="toast-region-bottom">
      <TransitionGroup name="toast-slide-up" tag="div" class="flex w-full max-w-[900px] flex-col gap-3">
        <ToastItem
          v-for="item in bottomItems"
          :key="item.id"
          :item="item"
          @dismiss="onDismiss"
        />
      </TransitionGroup>
    </div>
  </div>
</template>

<style scoped>
/* === slide-down（TOP 区进入/退出） === */
.toast-slide-down-enter-active {
  transition: all 0.3s ease-out;
}
.toast-slide-down-leave-active {
  transition: all 0.2s ease-in;
}
.toast-slide-down-enter-from {
  opacity: 0;
  transform: translateY(-24px);
}
.toast-slide-down-leave-to {
  opacity: 0;
  transform: translateY(-16px);
}

/* === slide-up（BOTTOM 区进入/退出） === */
.toast-slide-up-enter-active {
  transition: all 0.3s ease-out;
}
.toast-slide-up-leave-active {
  transition: all 0.2s ease-in;
}
.toast-slide-up-enter-from {
  opacity: 0;
  transform: translateY(24px);
}
.toast-slide-up-leave-to {
  opacity: 0;
  transform: translateY(16px);
}

/* === fade（CENTER 区） === */
.toast-fade-enter-active {
  transition: opacity 0.3s ease-out;
}
.toast-fade-leave-active {
  transition: opacity 0.2s ease-in;
}
.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
}
</style>
